import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaHome, FaUserShield, FaFileAlt, FaChevronDown, FaChevronUp,
    FaList, FaCalendarAlt, FaDollarSign, FaTv, FaUpload, FaBars, FaTimes, FaServer, FaSlidersH, FaLock,
    FaChartLine, FaChartBar, FaFileExcel
} from "react-icons/fa";
import { AiOutlineArrowLeft } from "react-icons/ai";
import { usePermisos } from "../hooks/usePermisos";

export default function Sidebar() {
    const [openMenu, setOpenMenu] = useState("");
    const [activeItem, setActiveItem] = useState("Inicio");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebar_collapsed");
        return saved === "true";
    });
    const navigate = useNavigate();

    const { hasMenuAccess } = usePermisos();

    // Emitir evento cuando cambia el estado de colapso
    useEffect(() => {
        localStorage.setItem("sidebar_collapsed", collapsed);
        window.dispatchEvent(new CustomEvent("sidebar-collapsed", { detail: collapsed }));
    }, [collapsed]);

    // Cierra el drawer al navegar
    const handleSelect = (label, path = "") => {
        setActiveItem(label);
        if (path) navigate(path);
        setMobileOpen(false);
    };



    const toggleMenu = (menu) => setOpenMenu((prev) => (prev === menu ? "" : menu));

    // Cierra si pasa a desktop (evita quedar abierto)
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const mainButton = (label, icon, hasSubmenu = false, path = "") => {
        const isActive = activeItem === label;
        return (
            <button
                onClick={() => {
                    if (hasSubmenu) toggleMenu(label);
                    if (path) handleSelect(label, path);
                }}
                className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition font-medium
          ${isActive ? "bg-cyan-700 text-white" : "hover:bg-slate-700 text-white/80"}`}
                aria-expanded={hasSubmenu ? openMenu === label : undefined}
                aria-controls={hasSubmenu ? `submenu-${label}` : undefined}
                title={collapsed ? label : ""}
            >
                <div className="flex items-center gap-3">
                    {icon}
                    {!collapsed && <span className="truncate">{label}</span>}
                </div>
                {hasSubmenu && !collapsed && (openMenu === label ? <FaChevronUp /> : <FaChevronDown />)}
            </button>
        );
    };

    const subItem = (label, icon, path = "") => {
        const isActive = activeItem === label;
        return (
            <button
                onClick={() => handleSelect(label, path)}
                className={`pl-10 pr-4 py-2 text-left text-sm rounded transition flex items-center gap-3
          ${isActive ? "text-white font-semibold" : "text-white/70 hover:text-white"}`}
                title={collapsed ? label : ""}
            >
                {icon}
                {!collapsed && <span className="truncate">{label}</span>}
            </button>
        );
    };

    const hasAdmonAccess =
        hasMenuAccess('admon_listado') ||
        hasMenuAccess('admon_financiero') ||
        hasMenuAccess('admon_procesador_csv');

    const hasCDRsAccess =
        hasMenuAccess('cdrs_calendario') ||
        hasMenuAccess('cdrs_monitor');

    const hasClearcomAccess =
        hasMenuAccess('clearcom_config') ||
        hasMenuAccess('clearcom_bloqueos');

    const hasAnalisisAccess =
        hasMenuAccess('analisis_cps') ||
        hasMenuAccess('analisis_trafico');

    return (
        <>
            {/* Botón hamburguesa (solo móvil) */}
            <button
                type="button"
                className="md:hidden fixed top-3 left-3 z-[60] flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white shadow-lg"
                onClick={() => setMobileOpen(true)}
                aria-label="Abrir menú"
            >
                <FaBars /><span className="text-xs">Menú</span>
            </button>

            {/* Overlay móvil */}
            {mobileOpen && (
                <div
                    className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-[1px] z-[49]"
                    onClick={() => setMobileOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Drawer/Sidebar */}
            <aside
                className={`
          fixed md:static inset-y-0 left-0 z-[50]
          ${collapsed ? 'md:w-20' : 'w-72 md:w-64'}
          bg-gradient-to-b from-slate-900 to-slate-800 text-white
          p-4 md:p-4
          shadow-xl md:shadow-md
          rounded-tr-xl rounded-br-xl
          transform transition-all duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          flex flex-col
        `}
                role="navigation"
                aria-label="Barra lateral"
            >
                {/* Header del sidebar */}
                <div className="relative">
                    {/* Botón cerrar (solo móvil) */}
                    <button
                        className="md:hidden absolute -right-2 -top-2 bg-slate-800 text-white rounded-full w-8 h-8 grid place-items-center shadow"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Cerrar menú"
                    >
                        <FaTimes />
                    </button>

                    {/* 🆕 Botón para colapsar con ícono de flecha animado */}
                    <button
                        className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 bg-slate-700 hover:bg-cyan-600 text-white rounded-full w-7 h-7 items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 group"
                        onClick={() => setCollapsed(!collapsed)}
                        aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
                        title={collapsed ? "Expandir menú" : "Colapsar menú"}
                    >
                        <AiOutlineArrowLeft
                            className={`text-lg transition-transform duration-300 ease-in-out ${collapsed ? 'rotate-180' : 'rotate-0'
                                } group-hover:scale-110`}
                        />
                    </button>

                    <div className={`text-xl font-bold mb-6 text-center tracking-wide select-none transition-all duration-300 ${collapsed ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                        }`}>
                        <div className="flex justify-center mb-1">
                            {[...Array(5)].map((_, i) => (
                                <span
                                    key={i}
                                    className="w-2 h-2 bg-cyan-400 rounded-full mx-[2px] transition-all duration-300"
                                    style={{
                                        transitionDelay: `${i * 50}ms`,
                                        transform: collapsed ? 'scale(0)' : 'scale(1)'
                                    }}
                                />
                            ))}
                        </div>
                        <span className="text-white">contacta</span>
                    </div>

                    {/* Logo simplificado cuando está colapsado */}
                    {collapsed && (
                        <div className="text-center mb-6 animate-fadeIn">
                            <div className="flex justify-center">
                                {[...Array(5)].map((_, i) => (
                                    <span
                                        key={i}
                                        className="w-1.5 h-1.5 bg-cyan-400 rounded-full mx-[1px] animate-pulse"
                                        style={{ animationDelay: `${i * 100}ms` }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* NAV */}
                <nav className={`flex flex-col gap-2 text-sm overflow-y-auto transition-all duration-300 ${collapsed ? 'items-center' : ''
                    }`}>
                    {hasMenuAccess('inicio') && mainButton("Inicio", <FaHome />, false, "/dashboard")}

                    {hasAdmonAccess && (
                        <>
                            {mainButton("Admon", <FaUserShield />, true)}
                            {!collapsed && (
                                <div
                                    id="submenu-Admon"
                                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out
                  ${openMenu === "Admon" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                                >
                                    <div className="overflow-hidden flex flex-col gap-1">
                                        {hasMenuAccess('admon_listado') && (
                                            <>
                                                {subItem("Listado", <FaList />, "/listado")}
                                                {(hasMenuAccess('admon_financiero') || hasMenuAccess('admon_procesador_csv')) && (
                                                    <div className="h-px bg-gray-600 my-1" />
                                                )}
                                            </>
                                        )}

                                        {hasMenuAccess('admon_financiero') && (
                                            <>
                                                {subItem("Financiero", <FaDollarSign />, "/admon/financiero")}
                                                {hasMenuAccess('admon_procesador_csv') && (
                                                    <div className="h-px bg-gray-600 my-1" />
                                                )}
                                            </>
                                        )}

                                        {hasMenuAccess('admon_procesador_csv') && (
                                            <>
                                                {subItem("Procesador CSV", <FaUpload />, "/admon/procesador-avanzado")}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {hasCDRsAccess && (
                        <>
                            {mainButton("CDRs", <FaFileAlt />, true)}
                            {!collapsed && (
                                <div
                                    id="submenu-CDRs"
                                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out
              ${openMenu === "CDRs" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                                >
                                    <div className="overflow-hidden flex flex-col gap-1">
                                        {hasMenuAccess('cdrs_calendario') && (
                                            <>
                                                {subItem("Calendario", <FaCalendarAlt />, "/cdr/calendario")}
                                                {hasMenuAccess('cdrs_monitor') && (
                                                    <div className="h-px bg-gray-600 my-1" />
                                                )}
                                            </>
                                        )}

                                        {hasMenuAccess('cdrs_monitor') &&
                                            subItem("Monitor", <FaTv />, "/cdr/monitor")
                                        }
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </nav>

                {hasClearcomAccess && (
                    <>
                        {mainButton("Clearcom", <FaServer />, true)}
                        {!collapsed && (
                            <div id="submenu-Clearcom"
                                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out
          ${openMenu === "Clearcom" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                                <div className="overflow-hidden flex flex-col gap-1">
                                    {hasMenuAccess('clearcom_config') && (
                                        <>
                                            {subItem("Configuración de cuentas", <FaSlidersH />, "/clearcom/configuracion")}
                                            {hasMenuAccess('clearcom_bloqueos') && <div className="h-px bg-gray-600 my-1" />}
                                        </>
                                    )}
                                    {hasMenuAccess('clearcom_bloqueos') &&
                                        subItem("Bloqueos", <FaLock />, "/clearcom/bloqueos")}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {hasAnalisisAccess && (
                    <>
                        {mainButton("Análisis", <FaChartLine />, true)}
                        {!collapsed && (
                            <div id="submenu-Analisis"
                                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out
          ${openMenu === "Análisis" ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                                <div className="overflow-hidden flex flex-col gap-1">
                                    {hasMenuAccess('analisis_cps') &&
                                        subItem("CPS y Sesiones", <FaChartBar />, "/analisis/cps")}
                                    {hasMenuAccess('analisis_trafico') &&
                                        subItem("Tráfico por result", <FaFileExcel />, "/analisis/trafico")}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Footer */}
                {!collapsed && (
                    <div className="mt-auto pt-3 text-[10px] text-white/60 transition-all duration-300">
                        <div className="border-t border-white/10 pt-3 text-center">
                            v1.0 • © contacta
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
}